import React from "react";

import type { TooltipProps } from "@/components/ui/tooltip";
import Tooltip from "@/components/ui/tooltip";

import { twMerge } from "tailwind-merge";

interface TooltipButtonProps {
  content: TooltipProps["content"];
  position?: TooltipProps["position"];
  children: React.ReactNode;
  ariaLabel?: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  dataTestId?: string;
  type?: "button" | "submit" | "reset";
  tooltipClassName?: string;
  arrowClassName?: string;
  disableTooltip?: boolean;
  sideOffset?: number;
  disabled?: boolean;
}

// Use forwardRef to allow parent components to access the button element
const TooltipButton = React.forwardRef<HTMLButtonElement, TooltipButtonProps>(
  (
    {
      content,
      position = "top-center",
      children,
      ariaLabel = "Tooltip Button",
      className,
      onClick,
      dataTestId,
      type = "button",
      tooltipClassName,
      arrowClassName,
      disableTooltip = false,
      disabled = false,
      sideOffset
    },
    ref
  ) => {
    const button = (
      <button
        ref={ref}
        type={type}
        aria-label={ariaLabel}
        className={twMerge(
          "flex items-center justify-center bg-transparent border-0 p-0",
          className
        )}
        onClick={onClick}
        data-testid={dataTestId}
        disabled={disabled}
      >
        {children}
      </button>
    );

    if (disableTooltip) return button;

    return (
      <Tooltip
        content={content}
        position={position}
        className={tooltipClassName}
        arrowClassName={arrowClassName}
        sideOffset={sideOffset}
      >
        {button}
      </Tooltip>
    );
  }
);

TooltipButton.displayName = "TooltipButton";

export default TooltipButton;
